import { FC, Fragment, useState } from "react";
import { Modal, Steps, Button, Space, Flex, Empty, InputNumber, Tooltip, Result, Spin, notification, Upload, UploadProps, Form, Input, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { PlusOutlined, DeleteOutlined, BarcodeOutlined, CheckCircleOutlined, ExclamationCircleTwoTone } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { Product } from "@/types/product";
import { WLocation, AddItemLocation, OrderV2, OrderAddV2, OrderAddItemV2, OrderItemV2 } from "@/types/inventoryV2";
import SText from "@/styles/SText";
import ProductSelectMulti from "@/components/Selector/ProductSelectMulti";
import BarcodeEditModal from "../InboundOrderForm/BarcodeEditModal";
import { LocationSelect } from "@/components/Selector";
import { getLocationIdx } from "@/utils/wLocation";
import { orderAdd, orderConfirm, orderDel, orderItemAdds, orderItemRms, orderLotUpdate, productRefLocations } from "@/apis/inventoryv2";
import apiErrorCodes from "@/apis/error";
import { ChangeEnum, OperateEnum } from "@/types/inventory";
import RefLocations from "@/components/Selector/RefLocations";
import { generateTemplate, parseData } from "@/pages/Requirement/components/EditRequirement/utils";
import { productList } from "@/apis/product";
import OrderDetailV2 from "../../../Order/components/OrderDetailV2";
import STableLayout from "@/styles/STableLayout";
import { AnyObject } from "antd/es/_util/type";
import { SaleOrderItemState } from "@/types/sales";
import { SourceTypeEnum } from "@/types/common";
import SaleOfficialOrderSelect from "./SaleOfficialOrderSelect";
import {
  buildInboundProductsFromSaleItems,
  clampInboundQuantity,
  getManulInboundStepIndexes,
  mergeInboundQuantities,
} from "./utils";
import { generateLotV2 } from "@/utils/codeParse";
import OrderConfirmInspection from "@/components/OrderConfirmInspection";
import { SpecialHandlingConfirm } from "@/types/inventoryV2";

interface ManulInboundProps {
  openType: ChangeEnum.TEMP_IN | ChangeEnum.PROD_IN | ChangeEnum.PROD_TEMP_IN;
  onCancel: () => void;
}

interface InboundLocation {
  itemLocId: string;
  location: WLocation;
  count: number;
  barcodes: Array<string>
}

interface ProductWithNum extends Partial<Product> {
  id: string;
  code: string;
  quantity?: number;
  // 仅生产入库使用：记录销售订单带入的最大可入库数量。
  maxQuantity?: number;
  // 仅生产入库使用：提交预入库单时回传销售订单条目来源。
  sourceId?: string;
  sourceType?: SourceTypeEnum.SALE_ITEM;
  locations?: InboundLocation[];
  refLocations?: WLocation[];
  lot?: string;
}

const ManulInbound: FC<ManulInboundProps> = ({ openType, onCancel }) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [products, setProducts] = useState<ProductWithNum[]>([]);
  const [selectedSaleOrder, setSelectedSaleOrder] = useState<SaleOrderItemState | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [openProductSelect, setOpenProductSelect] = useState(false);
  const [remark, setRemark] = useState<string>('');
  const [openCon, setOpenCon] = useState(false);

  const [addIng, setAddIng] = useState(false);
  const [orderResult, setOrderResult] = useState<OrderV2 | null>(null);

  // 库位相关状态
  const [currentEditingItemId, setCurrentEditingItemId] = useState<string | null>(null);
  const [changingLocation, setChangingLocation] = useState<{
    itemId: string | null;
    locationId: string | null;
  }>({ itemId: null, locationId: null });
  const [barcodeModalInfo, setBarcodeModalInfo] = useState<{
    open: boolean;
    itemId: string | null;
    locationId: string | null;
    barcodes: string[];
  }>({ open: false, itemId: null, locationId: null, barcodes: [] });
  const [uploading, setUploading] = useState<boolean>(false);
  const [errorCodes, setErrorCodes] = useState<string[]>([]);
  const [openDel, setOpenDel] = useState(false);
  const [openInspection, setOpenInspection] = useState(false);
  const [inspectionItems, setInspectionItems] = useState<OrderItemV2[]>([]);
  // 生产入库比临时入库多一个“选择销售订单”前置步骤，所以步骤索引要动态计算。
  const saleOrderRequired = openType === ChangeEnum.PROD_IN;
  const stepIndexes = getManulInboundStepIndexes(saleOrderRequired);

  const handlePrev = () => {
    setCurrent(current - 1);
  };

  const handleNext = async () => {
    if (current === stepIndexes.saleOrder) {
      if (!selectedSaleOrder) {
        notification.error({
          message: t("product.plSelectSaleOrder"),
        });
        return;
      }

      // 销售订单只负责生成商品列表；后面的入库、库位和预入库流程沿用原逻辑。
      const nextProducts = buildInboundProductsFromSaleItems(selectedSaleOrder.items);
      setProducts(nextProducts);
      setSelectedRowKeys([]);
      setErrorCodes([]);
      setCurrent(stepIndexes.product);
      return;
    }

    // 从产品步骤进入库位步骤时，获取推荐库位
    if (current === stepIndexes.product) {
      // 检查是否有 quantity 为空或 0 的商品
      const invalidProducts = products.filter(p => !p.quantity || p.quantity === 0 || !!!p.lot);
      if (invalidProducts.length > 0) {
        notification.error({
          message: t('warehouse.plInQuantityLot'),
          description: invalidProducts.map(p => p.code).join(','),
        });
        return;
      }
      try {
        // 依次为每个产品获取推荐库位
        setAddIng(true);
        const updatedProducts = await Promise.all(
          products.map(async (product) => {
            try {
              let refLocations = await productRefLocations(product.id);
              refLocations = refLocations?.sort((a, b) => (a.count || 0) - (b.count || 0)) || []
              return {
                ...product,
                locations: refLocations[0] ? [
                  {
                    ...refLocations[0],
                    itemLocId: refLocations[0].id,
                    location: refLocations[0],
                    count: product.quantity || 0,
                    barcodes: []
                  }
                ] : [],
                refLocations,
              };
            } catch (err) {
              // 获取失败时，设置为空数组
              console.error(`Failed to fetch refLocations for product ${product.id}:`, err);
              return { ...product, refLocations: [] };
            }
          })
        );
        setProducts(updatedProducts);
        setCurrent(stepIndexes.location);
      } catch (err) {
        notification.error({
          message: t('common.error'),
          description: apiErrorCodes[err as keyof typeof apiErrorCodes] || err + '',
        });
      } finally {
        setAddIng(false)
      }
      return;
    }

    // 第二步点击下一步时，提交订单
    if (current === stepIndexes.location) {
      // 验证每个物料是否有库位
      const invalidItems = products.filter(p => !p.locations || p.locations.length === 0);
      if (invalidItems.length > 0) {
        notification.error({
          message: t('warehouse.locationsRequired'),
        });
        return;
      }

      // 验证库位数量是否有效（不为0或空）
      const invalidCountItems: { productName: string; locationName: string }[] = [];
      products.forEach(p => {
        (p.locations || []).forEach((loc, index) => {
          if (loc.count === 0 || loc.count === null || loc.count === undefined) {
            const locationName = getLocationIdx(loc.location);
            invalidCountItems.push({
              productName: p.code,
              locationName
            });
          }
        });
      });

      if (invalidCountItems.length > 0) {
        const errorList = invalidCountItems.map(item => `${item.productName}: ${item.locationName}`);
        notification.error({
          message: t('warehouse.locationCountRequired'),
          description: errorList.join('; ')
        });
        return;
      }
      // 验证库位数量是否有效（不为0或空）
      const invalidQUantityItems: string[] = [];
      products.forEach(p => {
        const totalCount = p?.locations?.reduce((sum, loc) => sum + loc.count, 0) || 0;
        if (totalCount !== p.quantity) invalidQUantityItems.push(p.code)
      });

      if (invalidQUantityItems.length > 0) {
        notification.error({
          message: t('warehouse.locNumnotOrder'),
          description: invalidQUantityItems.join(', ')
        });
        return;
      }


      setAddIng(true);
      try {
        let now = Date.now();
        let noLot: string[] = [];
        //构建新的orderItem
        let newOrderItems: Array<OrderAddItemV2> = products.map(p => {
          const locations: AddItemLocation[] = (p.locations || []).map(loc => ({
            locationId: loc.location.id,
            count: loc.count,
            barcodes: loc.barcodes,
          }));
          let lotStr = generateLotV2(p.code, '', ++now);
          if (!!!lotStr) noLot.push(p.code)
          const item: OrderAddItemV2 = {
            productId: p.id,
            count: p.quantity || 0,
            lot: lotStr,
            thdlot: "",
            desc: "",
            locations,
            sourceId: saleOrderRequired ? p.sourceId : "",
            sourceType: saleOrderRequired ? p.sourceType : undefined,
          };
          return item;
        });
        if (noLot.length > 0) {
          notification.error({
            message: t('common.lotGetErr'),
            description: t('purchase.printCode') + ": " + noLot.join(',')
          })
          setAddIng(false);
          return;
        }
        //新增单据
        if (!!!orderResult) {
          // 构造 OrderAddV2 数据
          const orderData: OrderAddV2 = {
            type: ChangeEnum.PRE_IN,
            opType: openType === ChangeEnum.TEMP_IN ? OperateEnum.TEMPORARY : OperateEnum.PROD_IN,
            desc: remark,
            preTime: null,
            arrivalAt: null,
            items: newOrderItems,
          };
          if (!!selectedSaleOrder) {
            orderData['sourceId'] = selectedSaleOrder.id;
            orderData['sourceType'] = SourceTypeEnum.SALE;
          }
          const result = await orderAdd(orderData);        
          setOrderResult(result);
          notification.success({
            message: t('warehouse.delInSucc', { number: result.number }),
            description: result.number
          })
        } else {
          for (let i = 0; i < orderResult.items.length; i++) {
            const dId = orderResult.items[i].id;
            await orderItemRms(orderResult.id, [dId]);
          }
          let newItems = await orderItemAdds(orderResult.id, newOrderItems);
          notification.success({
            message: t('warehouse.inboundUpdateSucc'),
            description: orderResult.number
          });
          setOrderResult({
            ...orderResult,
            items: [...newItems]
          })
        }
        setCurrent(stepIndexes.preInventory);
      } catch (err) {
        if (err === 70606) {
          notification.error({
            message: t('warehouse.inFail'),
            description: t('apiError.error26'),
          });
        } else if (err === 73010) {
          // 其他错误
          notification.error({
            placement: 'top',
            message: t('warehouse.inboundFailed'),
            description: t('warehouse.locFailed'),
          });
        } else notification.error({
          message: t('warehouse.inFail'),
          description: err + '',
        });
      } finally {
        setAddIng(false);
      }
    }
    if (current === stepIndexes.preInventory) {
      setOpenCon(true);
    }
  };

  // 确认弹窗确定后，打开巡检弹窗
  const handleConfirmModalOk = () => {
    setOpenCon(false);
    setInspectionItems(orderResult?.items || []);
    setOpenInspection(true);
  };

  // 巡检弹窗 onOk 后执行入库确认
  const doConfirm = async (specialHandling: SpecialHandlingConfirm[]) => {
    setAddIng(true);
    try {
      await orderConfirm(orderResult?.id ?? '', 0, specialHandling);
      // try {
      //   await orderConfirm(orderResult?.id ?? '', 0, specialHandling);
      // } catch (error: any) {
      //   if (error?.code === 70845) await orderConfirm(orderResult?.id ?? '', 0);
      //   else throw error
      // }
      notification.success({
        message: t('inventory.inboundSuccess'),
        description: orderResult?.number
      })
      setCurrent(stepIndexes.complete);
    } catch (error) {
      notification.error({
        message: t('warehouse.inboundFailed'),
        description: error + ''
      })
    } finally {
      setAddIng(false)
    }
  };

  const handleAddProducts = (selectedProducts: ProductWithNum[]) => {
    const productMap = new Map<string, ProductWithNum>();
    products.forEach(p => productMap.set(p.code, p));
    let now = Date.now();
    selectedProducts.forEach(p => {
      const existing = productMap.get(p.code);
      if (existing) {
        // 保持原来的“同商品合并”逻辑；生产入库时再额外受 maxQuantity 限制。
        productMap.set(p.code, {
          ...existing,
          quantity: mergeInboundQuantities(existing.quantity ?? 0, p.quantity ?? 0, existing.maxQuantity),
          maxQuantity: existing.maxQuantity ?? p.maxQuantity,
        });
      } else {
        productMap.set(p.code, { ...p, quantity: p.quantity ?? 0, lot: generateLotV2(p.code, '', ++now) });
      }
    });
    setProducts(Array.from(productMap.values()));
  };

  const handleDeleteSelected = () => {
    setErrorCodes(last => last.filter(code => !selectedRowKeys.includes(code)))
    setProducts(products.filter(p => !selectedRowKeys.includes(p.id)));
    setSelectedRowKeys([]);
  };

  // 处理库位数量变更
  const handleLocationCountChange = (
    value: number | null,
    recordId: string,
    locationId: string,
    currentBarcodes: string[],
  ) => {
    if (value !== null && value !== undefined) {
      // 检查当前条码数量
      if (currentBarcodes.length > value) {
        notification.warning({
          message: t('warehouse.checkBarcodes'),
          description: t('warehouse.barcodesExceedNewCount')
        });
        return;
      }

      setProducts(prev => prev.map(item => {
        if (item.id === recordId) {
          return {
            ...item,
            locations: item.locations?.map(loc => {
              if (loc.location.id === locationId) {
                return { ...loc, count: value };
              }
              return loc;
            })
          };
        }
        return item;
      }));
    }
  };

  // 处理库位选择变更
  const handleLocationSelectChange = (location: WLocation) => {
    // 处理更换库位的逻辑
    if (changingLocation.itemId && changingLocation.locationId) {
      const currentItem = products.find(item => item.id === changingLocation.itemId);
      const isLocationExist = currentItem?.locations?.some(
        loc => loc.location.id === location.id
      );

      if (isLocationExist) {
        notification.warning({ message: t('warehouse.locationExist') });
        return;
      }

      setProducts(prev => prev.map(item => {
        if (item.id === changingLocation.itemId) {
          return {
            ...item,
            locations: item.locations?.map(loc => {
              if (loc.location.id === changingLocation.locationId) {
                return { ...loc, location };
              }
              return loc;
            })
          };
        }
        return item;
      }));
      setChangingLocation({ itemId: null, locationId: null });
      return;
    }

    // 处理添加新库位的逻辑
    if (currentEditingItemId && location) {
      const currentItem = products.find(item => item.id === currentEditingItemId);
      if (currentItem?.refLocations?.length && !!!currentItem?.refLocations?.find(loc => loc.id === location.id)) {
        notification.warning({
          message: t('warehouse.notRecommendedLocation')
        })
      }
      const isLocationExist = currentItem?.locations?.some(
        loc => loc.location.id === location.id
      );

      if (isLocationExist) {
        notification.warning({ message: t('warehouse.locationExist') });
        return;
      }

      const newLocation: InboundLocation = {
        itemLocId: `${currentEditingItemId}-${location.id}`,
        location,
        count: 0,
        barcodes: []
      };

      setProducts(prev => prev.map(item => {
        if (item.id === currentEditingItemId) {
          return {
            ...item,
            locations: [...(item.locations || []), newLocation]
          };
        }
        return item;
      }));
      setCurrentEditingItemId(null);
    }
  };

  // 处理条码编辑确认
  const handleBarcodeEditOk = (barcodes: string[]) => {
    if (barcodeModalInfo.itemId && barcodeModalInfo.locationId) {
      const currentItem = products.find(item => item.id === barcodeModalInfo.itemId);
      const currentLocation = currentItem?.locations?.find(
        loc => loc.location.id === barcodeModalInfo.locationId
      );

      let finalBarcodes = barcodes;

      // 如果条码数量超过 count，自动截取并提示
      if (currentLocation && barcodes.length > currentLocation.count) {
        finalBarcodes = barcodes.slice(0, currentLocation.count);
        notification.warning({
          message: t('warehouse.barcodesAutoTrimmed'),
          description: t('warehouse.barcodesAutoTrimmedDesc', { count: currentLocation.count })
        });
      }

      setProducts(prev => prev.map(item => {
        if (item.id === barcodeModalInfo.itemId) {
          return {
            ...item,
            locations: item.locations?.map(loc => {
              if (loc.location.id === barcodeModalInfo.locationId) {
                return { ...loc, barcodes: finalBarcodes };
              }
              return loc;
            })
          };
        }
        return item;
      }));
    }
    setBarcodeModalInfo({ open: false, itemId: null, locationId: null, barcodes: [] });
  };

  // 删除库位
  const handleDeleteLocation = (recordId: string, locationId: string) => {
    setProducts(prev => prev.map(item => {
      if (item.id === recordId) {
        return {
          ...item,
          locations: item.locations?.filter(loc => loc.location.id !== locationId)
        };
      }
      return item;
    }));
  };

  // 处理推荐库位选择变更
  const handleRefLocationsChange = (recordId: string, selectedLocations: WLocation[]) => {
    setProducts(prev => prev.map(item => {
      if (item.id === recordId) {
        const refLocationIds = new Set((item.refLocations || []).map(loc => loc.id));
        const selectedIds = new Set(selectedLocations.map(loc => loc.id));

        // 最终保留的库位：
        // 1. 非推荐库位的（不在 refLocationIds 中）保持不变
        // 2. 推荐库位中被选中的（在 selectedIds 中）
        const newLocations = (item.locations || []).filter(loc => {
          const isRefLocation = refLocationIds.has(loc.location.id);
          const isSelected = selectedIds.has(loc.location.id);
          // 非推荐库位，或者推荐库位中被选中
          return !isRefLocation || isSelected;
        });

        // 添加新选中的推荐库位
        const toAdd = selectedLocations.filter(loc => {
          const alreadyExists = (item.locations || []).some(existingLoc => existingLoc.location.id === loc.id);
          return !alreadyExists;
        }).map(loc => ({
          itemLocId: `${recordId}-${loc.id}`,
          location: loc,
          count: 0,
          barcodes: []
        }));

        return {
          ...item,
          locations: [...newLocations, ...toAdd]
        };
      }
      return item;
    }));
  };

  // 下载模版
  const handleSaveTemplate = () => {
    generateTemplate(t, t('warehouse.productInput'));
  };

  // 导入数据
  const handleImport: UploadProps['beforeUpload'] = async (file) => {
    setUploading(true);
    try {
      const addList = await parseData(file, t);
      if (addList.length > 0) {
        const productListRes = await productList({ params: { strs: addList.map(a => a.product.code) } });
        const productListMap: Record<string, Product> = {};
        productListRes.body.forEach(p => {
          productListMap[p.code] = { ...p }
        });
        let notProsList: string[] = [];
        for (let index = 0; index < addList.length; index++) {
          let code = addList[index].product.code
          if (!!productListMap[code]) {
            addList[index].product = { ...productListMap[code] }
          } else notProsList.push(code)
        }
        if (notProsList.length > 0) {
          setErrorCodes(notProsList)
          notification.error({
            message: t('apiError.error26'),
            description: notProsList.join(', ')
          })
        }
        handleAddProducts(addList.map(pro => ({
          ...pro.product,
          quantity: pro.useAmount
        })))
        notification.success({ placement: 'top', message: t("requirement.parseDataSuccess", { count: addList.length }) });
      }
    } catch (error) {
      notification.error({ placement: 'top', message: t("requirement.invalidTemplate") });
    } finally {
      setUploading(false);
    }
    return false; // 阻止自动上传
  };

  const closeModal = () => {
    if (current !== stepIndexes.complete && !!orderResult) setOpenDel(true);
    else onCancel();
  }

  const delClose = async () => {
    setOpenDel(false);
    try {
      setAddIng(true);
      await orderDel(orderResult?.id ?? "");
      notification.success({
        message: t('common.deleteSuccess'),
        description: orderResult?.number
      })
      onCancel();
    } catch (error) {
      notification.error({
        message: t('order.optErr')
      })
    }
  }
  // 统一列配置
  const columns: ColumnsType<ProductWithNum> = [
    {
      title: t("warehouse.productCode"),
      dataIndex: "code",
      key: "code",
      width: 120,
      render: (text: string) => <SText style={{ width: 120, color: errorCodes.includes(text) ? "red" : "unset" }} text={text} />,
    },
    {
      title: t("warehouse.productName"),
      dataIndex: "name",
      key: "name",
      width: 160,
      render: (text: string) => <SText style={{ width: 160 }} text={text} />,
    },
    {
      title: t("warehouse.productModel"),
      dataIndex: "model",
      key: "model",
      width: 120,
      ellipsis: true,
      render: (text: string) => <SText text={text} />,
    },
    {
      title: t("warehouse.uom"),
      dataIndex: "uom",
      key: "uom",
      width: 80,
      render: (text: string) => <SText text={text} />,
    },
    {
      title: 'lot',
      dataIndex: "lot",
      key: "lot",
      width: 140,
      render: (value: string, record) => {
        if (current === stepIndexes.product) {
          return (
            <Input
              value={record.lot}
              onChange={(val) => {
                setProducts(prev => prev.map(p => p.id === record.id ? {
                  ...p,
                  lot: val.target.value,
                } : p));
              }}
              placeholder="lot"
              suffix={
                <Button onClick={() => {
                  let lotStr = generateLotV2(record?.code ?? '', '', Date.now());
                  setProducts(prev => prev.map(p => p.id === record.id ? {
                    ...p,
                    lot: lotStr,
                  } : p));
                }} >{t('common.genLot')}</Button>
              }
            />
          );
        }
        return <SText text={(value ?? 0) + ''} />;
      },
    },
    {
      title: t("warehouse.inQuantity"),
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (value: number, record) => {
        if (current === stepIndexes.product) {
          return (
            <InputNumber changeOnWheel
              value={value ?? 0}
              min={0}
              // 生产入库带入的商品数量允许调整，但不能超过销售订单带入数量。
              max={record.maxQuantity}
              onChange={(val) => {
                setProducts(prev => prev.map(p => p.id === record.id ? {
                  ...p,
                  quantity: clampInboundQuantity(val, record.maxQuantity),
                } : p));
              }}
            />
          );
        }
        return <SText text={(value ?? 0) + ''} />;
      },
    },
    {
      title: t("warehouse.location"),
      key: "locations",
      width: 300,
      hidden: current === stepIndexes.product,
      render: (_, record) => {
        const totalCount = record?.locations?.reduce((sum, loc) => sum + loc.count, 0) || 0;
        return (
          <Flex gap={8} vertical>
            {record.locations?.map((loc) => {
              let isNotRef = record.refLocations && record.refLocations.length > 0
                && !!!record.refLocations?.find(l => l.id === loc.location.id)
              return (
                <div key={`${record.id}-${loc.location.id}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <InputNumber changeOnWheel
                    addonBefore={
                      <Tooltip title={isNotRef ? t('warehouse.isNotRef') : t('warehouse.changeLocation')}>
                        <div
                          style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            display: 'inline-block',
                            width: '110px',
                            wordBreak: "break-all",
                            whiteSpace: "normal",
                            color: isNotRef ? "orange"
                              : "unset"
                          }}
                          onClick={() => {
                            setChangingLocation({
                              itemId: record.id,
                              locationId: loc.location.id
                            });
                          }}
                        >
                          {getLocationIdx(loc.location)}
                        </div>
                      </Tooltip>
                    }
                    addonAfter={
                      <Tooltip title={t('warehouse.barcodeCount')}>
                        <span>{loc.barcodes.length}</span>
                      </Tooltip>
                    }
                    controls={false}
                    value={loc.count}
                    min={0}
                    max={(record.quantity ?? 0) - (record.locations || []).reduce((sum, l) => sum + (l.location.id === loc.location.id ? 0 : l.count), 0)}
                    style={{ height: "100%" }}
                    onChange={(value) => handleLocationCountChange(
                      value,
                      record.id,
                      loc.location.id,
                      loc.barcodes,
                    )}
                  />
                  <Tooltip title={t('warehouse.editBarcodes')}>
                    <Button
                      type="text"
                      icon={<BarcodeOutlined />}
                      size="small"
                      onClick={() => {
                        setBarcodeModalInfo({
                          open: true,
                          itemId: record.id,
                          locationId: loc.location.id,
                          barcodes: loc.barcodes || []
                        });
                      }}
                    />
                  </Tooltip>
                  <Tooltip title={t('common.del')}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      size="small"
                      onClick={() => handleDeleteLocation(record.id, loc.location.id)}
                    />
                  </Tooltip>
                </div>
              )
            }
            )}
            <Flex gap={6} style={{ width: "100%" }}>
              <Tooltip title={t('warehouse.addLocation')}>
                <Button
                  disabled={totalCount === record.quantity}
                  type="dashed"
                  icon={<PlusOutlined />}
                  size="small"
                  style={{ flex: 1 }}
                  onClick={() => setCurrentEditingItemId(record.id)}
                >
                  {t('common.add')}
                </Button>
              </Tooltip>
              <RefLocations refLocations={record.refLocations || []} onChangeRefLocation={(selectedLocations) => handleRefLocationsChange(record.id, selectedLocations)} values={record.locations?.map(loc => loc.location.id)} />
            </Flex>
          </Flex>
        );
      }
    },
  ];

  const steps = [
    ...(saleOrderRequired ? [{
      title: t("product.selectSaleOrder"),
      content: (
        <SaleOfficialOrderSelect value={selectedSaleOrder} onChange={setSelectedSaleOrder} />
      ),
    }] : []),
    {
      title: t("warehouse.selectProduct"),
      content: (
        <Fragment>
          {/* 生产入库的商品来自前一步销售订单，这里保留表格编辑，不再开放手动增删导入。 */}
          {!saleOrderRequired && (
            <Flex gap={10} style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setOpenProductSelect(true)}
              >
                {t("warehouse.addProduct")}
              </Button>
              <Upload
                accept=".xlsx,.xls"
                beforeUpload={handleImport}
                showUploadList={false}
                disabled={uploading}
              >
                <Button loading={uploading}>{t("requirement.inputTemp")}</Button>
              </Upload>
              <Button
                onClick={handleSaveTemplate}
              >
                {t('requirement.downloadTemplate')}
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteSelected}
                disabled={selectedRowKeys.length === 0}
              >
                {t("warehouse.delete")}
              </Button>
            </Flex>
          )}
          <STableLayout
            headerChildren={
              <Form.Item label={t('purchase.remark')} style={{ width: "100%" }}>
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} value={remark} onChange={e => setRemark(e.target.value)} maxLength={500} showCount />
              </Form.Item>
            }
            columns={columns as Array<AnyObject>}
            dataSource={products}
            total={products.length}
            rowKey="id"
            pagination={false}
            locale={{ emptyText: <Empty description={t("warehouse.noData")} /> }}
            rowSelection={!saleOrderRequired ? {
              selectedRowKeys,
              onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
            } : undefined}
          />
        </Fragment>
      ),
    },
    {
      title: t("warehouse.editLocation"),
      content: (
        <STableLayout
          headerChildren={null}
          columns={columns as Array<AnyObject>}
          dataSource={products}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: <Empty description={t("warehouse.noData")} /> }}
          total={products.length}
          scrollX={1000}
        />
      ),
    },
    {
      title: t("purchase.preInventory"),
      content: (
        <Fragment>
          <Typography.Title level={4} style={{ marginTop: 0 }} >{orderResult?.number}</Typography.Title>
          <OrderDetailV2 noModal data={orderResult || undefined} handleCancel={() => { }} handleRm={() => { }} />
        </Fragment>
      ),
    },
    {
      title: t("warehouse.complete"),
      content: (
        <div style={{ minHeight: "400px", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title={orderResult ? `${orderResult.number} ${t('warehouse.inboundComplete')}` : t('warehouse.inboundComplete')}
            subTitle={t('warehouse.inboundSuccessDesc')}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={t(openType === ChangeEnum.TEMP_IN ? "warehouse.tempIn" : openType === ChangeEnum.PROD_TEMP_IN ? "warehouse.prodTempIn" : "warehouse.proIn")}
        open={openType !== undefined}
        onCancel={closeModal}
        width={1200}
        centered
        maskClosable={false}
        footer={
          <Flex justify="space-between" style={{ width: "100%" }}>
            <div>
              <Space>
                {current === stepIndexes.saleOrder && (
                  <Button type="primary" disabled={!selectedSaleOrder} onClick={handleNext}>
                    {t("warehouse.next")}
                  </Button>
                )}
                {current === stepIndexes.product && (
                  <>
                    {saleOrderRequired && <Button onClick={handlePrev}>{t("warehouse.previous")}</Button>}
                    <Button type="primary" disabled={products.length === 0 || errorCodes.length > 0} loading={addIng} onClick={handleNext}>
                      {t("warehouse.next")}
                    </Button>
                  </>
                )}
                {current === stepIndexes.location && (
                  <>
                    <Button onClick={handlePrev}>{t("warehouse.previous")}</Button>

                    <Button type="primary" loading={addIng} onClick={handleNext}>
                      {t("warehouse.next")}
                    </Button>
                  </>
                )}
                {current === stepIndexes.preInventory && <Button onClick={handlePrev}>{t("warehouse.previous")}</Button>}
              </Space>
            </div>
            {current === stepIndexes.preInventory && <Button type="primary" onClick={() => setOpenCon(true)}>{t("warehouse.confirm")}</Button>}
            {current === stepIndexes.complete && <Button type="primary" onClick={onCancel}>
              {t("warehouse.confirm")}
            </Button>}
          </Flex>
        }
      >
        <Spin spinning={addIng}>
          <Steps current={current} items={steps.map(item => ({ title: item.title }))} style={{ marginBottom: 10 }} />
          <Flex vertical style={{ width: "100%", height: "70vh", overflow: "hidden" }}>
            {steps[current].content}
          </Flex>
        </Spin>
      </Modal>

      <ProductSelectMulti
        openProduct={openProductSelect}
        setOpenProduct={setOpenProductSelect}
        values={products as Product[]}
        handleOk={handleAddProducts}
        isQuantity={false}
      />

      <LocationSelect
        open={!!currentEditingItemId || !!changingLocation.itemId}
        value={changingLocation?.locationId || undefined}
        onChange={handleLocationSelectChange}
        onCancel={() => {
          setCurrentEditingItemId(null);
          setChangingLocation({ itemId: null, locationId: null });
        }}
      />

      <BarcodeEditModal
        open={barcodeModalInfo.open}
        initialBarcodes={barcodeModalInfo.barcodes}
        onOk={handleBarcodeEditOk}
        onCancel={() => {
          setBarcodeModalInfo({ open: false, itemId: null, locationId: null, barcodes: [] });
        }}
      />
      <Modal title={
        <Space>
          <ExclamationCircleTwoTone />
          <span>{t('common.tip')}</span>
        </Space>
      } open={openCon} onOk={handleConfirmModalOk} onCancel={() => setOpenCon(false)}>
        {t('inventory.conIn')}
      </Modal>
      <Modal title={
        <Space>
          <ExclamationCircleTwoTone />
          <span>{t('common.tip')}</span>
        </Space>
      } open={openDel} onOk={() => { delClose(); }} onCancel={() => { setOpenDel(false); onCancel() }}
        okText={t('warehouse.delOrderClose')}
        cancelText={t('warehouse.notDelClose')}
        okButtonProps={{ danger: true }}
      >
        {t('warehouse.delInTip', { number: orderResult?.number })}
      </Modal>
      {/* 巡检弹窗：onOk 后执行入库确认逻辑 */}
      <OrderConfirmInspection
        items={inspectionItems}
        open={openInspection}
        onOk={(data) => {
          setOpenInspection(false);
          doConfirm(data);
        }}
        onClose={() => setOpenInspection(false)}
      />
    </>
  );
};

export default ManulInbound;
